import {
	checkOverridesValueMappingProfile,
	checkOverridesValueMappingProfilesRejected,
	createValueMappingProfile,
	getValuemappingSensorsList,
	setValuemappingProfileDurationDialogOpen,
} from '@Actions';
import {
	ValuemappingDurationDialog,
	ValuemappingOverridesWarningDialog,
	ValuemappingProfileCard,
} from '@Components';
import { ValuemappingProfileForm } from '@Components/ValuemappingProfileForm';
import { useNavigateTo, useYupSchemaValidation } from '@Hooks';
import { error, LoadingPage } from '@innovyze/stylovyze';
import ValuemappingProfileSchema from '@Pages/shared/ValuemappingProfileSchema';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations';
import { MAIN_ROUTES } from '@Types/routes';
import { ValuemappingProfileFormShape } from '@Types/valuemappingForm.types';
import { determineOverridesMessage } from '@Utils/overridesMessage';
import { createValuemappingProfileCreateRequest } from '@Utils/valuemappingFormHelpers';
import { FormikErrors, FormikProps } from 'formik';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ControlsButtons from './ControlsButtons';
import {
	BodyGridStyled,
	PageWrapperStyled,
} from './CreateValuemappingProfile.styles';

const CreateValuemappingProfile = () => {
	useNavigateTo();
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const formRef = useRef<FormikProps<ValuemappingProfileFormShape>>();
	const [formErrors, setFormErrors] = React.useState<
		FormikErrors<ValuemappingProfileFormShape>
	>({});
	const pageLoading = useMemo<boolean>(() => false, []);
	const [schemaValidate] =
		useYupSchemaValidation<ValuemappingProfileFormShape>();

	const {
		valueMappingProfiles: {
			initialized,
			valueMappingDetail: {
				multiselect: {
					sensorIds: {
						options: sensorsList,
						initialized: sensorIdsListInitialized,
					},
				},
			},
			overrides: { list: overridesList },
			durationDialogOperation,
		},
	} = useSelector(getValue);

	const showOverridesDialog = useMemo(
		() => !!overridesList.length,
		[overridesList],
	);

	const overridesDialogMessages = useMemo(
		() =>
			determineOverridesMessage(
				overridesList,
				sensorsList,
				formRef.current?.values,
			),
		[overridesList],
	);

	const handleClickOnBackButton = (): void => {
		history.push(MAIN_ROUTES.ValueMapping);
		dispatch(checkOverridesValueMappingProfilesRejected());
	};

	useEffect(() => {
		dispatch(getValuemappingSensorsList());
	}, []);

	const handleClickOnButtonSave = (): void => {
		if (pageLoading) {
			dispatch(error('Page is still loading'));
		} else {
			handleFormValidation();
		}
	};

	const handleFormValidation = (): void => {
		if (!formRef.current) return;
		const validationResult = schemaValidate(
			formRef.current.values,
			ValuemappingProfileSchema(t),
		);
		if (validationResult.hasErrors) {
			setFormErrors(validationResult.errors as never);
		} else {
			setFormErrors({}); // ? unset errors
			handleCheckOverrides();
		}
	};

	const handleCheckOverrides = (): void => {
		if (!formRef.current) return;
		dispatch(
			checkOverridesValueMappingProfile({
				body: createValuemappingProfileCreateRequest(
					formRef.current.values,
				),
			}),
		);
		dispatch(setValuemappingProfileDurationDialogOpen('NONE'));
	};

	const handleClickOnCancelButtonOverridesDialog = (): void => {
		dispatch(checkOverridesValueMappingProfilesRejected());
	};

	const handleClickOnConfirmDurationDialog = (): void => {
		if (!formRef.current) return;
		handleClickOnCancelButtonDurationDialog();
		dispatch(
			createValueMappingProfile(
				createValuemappingProfileCreateRequest(formRef.current.values),
			),
		);
	};

	const handleClickOnConfirmButtonOverridesDialog = (): void => {
		dispatch(setValuemappingProfileDurationDialogOpen('UPDATE'));
	};

	const handleClickOnCancelButtonDurationDialog = (): void => {
		dispatch(checkOverridesValueMappingProfilesRejected());
		dispatch(setValuemappingProfileDurationDialogOpen('NONE'));
	};

	return (
		<PageWrapperStyled
			title={t('Create Value Mapping')}
			// back={{
			// 	action: handleClickOnBackButton,
			// 	label: 'Back to Value Mapping List',
			// }}
			secondary={
				<ControlsButtons
					onClickCancelButton={handleClickOnBackButton}
					onClickSaveButton={handleClickOnButtonSave}
					saveDisabled={!initialized}
				/>
			}
			isLoading={pageLoading}
			applyPadding
			headerUnderline>
			{!initialized && <LoadingPage />}
			<BodyGridStyled hidden={!initialized}>
				<ValuemappingProfileCard>
					<ValuemappingProfileForm
						formRef={formRef as unknown as never}
						disabledFields={{
							sensorIds: !sensorIdsListInitialized,
						}}
						initialValues={{
							profileName: '',
							sensorTypes: [],
							sensorIds: [],
							mappings: [
								{
									order: 0,
									from: null,
									method: 'Last Known Value',
									to: '',
								},
								{
									order: 1,
									from: null,
									method: 'Last Known Value',
									to: '',
								},
							],
						}}
						errors={formErrors}
					/>
				</ValuemappingProfileCard>
			</BodyGridStyled>
			<ValuemappingOverridesWarningDialog
				open={showOverridesDialog}
				onConfirm={handleClickOnConfirmButtonOverridesDialog}
				onCancel={handleClickOnCancelButtonOverridesDialog}
				messages={overridesDialogMessages}
				confirmButtonDisabled={!initialized}
			/>

			<ValuemappingDurationDialog
				open={durationDialogOperation !== 'NONE'}
				onConfirm={handleClickOnConfirmDurationDialog}
				onCancel={handleClickOnCancelButtonDurationDialog}
			/>
		</PageWrapperStyled>
	);
};

export default CreateValuemappingProfile;
